import React, {useState, useMemo, useEffect} from 'react';
import {Box, Menu, MenuItem, Tooltip, Typography, InputBase} from '@mui/material';
import {
    CorporateFareOutlined,
    ApartmentOutlined,
    KeyboardArrowDown,
    Check,
    Search,
    Close,
} from '@mui/icons-material';
import {usePermissions, useTranslate} from 'react-admin';
import {t} from '../../themes/statamicTokens';
import {useScope} from '../../scope/ScopeContext';
import {useUmbrellaChoices} from '../../lib/customHooks';
import useUmbrellasAndCharities from '../../filter/useUmbrellasAndCharities';
import {useChromeMode} from '../../themes/ThemeModeContext';

const ROW_HEIGHT = 38;

const ScopeRow = ({icon: Icon, label, active, onClick, locked, indent}) => {
    const {chrome, sidebarCollapsed} = useChromeMode();

    const row = (
        <Box
            onClick={locked ? undefined : onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                gap: sidebarCollapsed ? 0 : 1,
                height: ROW_HEIGHT,
                px: sidebarCollapsed ? 0 : 1.25,
                pl: sidebarCollapsed ? 0 : (indent ? 2.5 : 1.25),
                mx: sidebarCollapsed ? 0.5 : 1,
                borderRadius: `${t.radius.sm}px`,
                cursor: locked ? 'default' : 'pointer',
                color: chrome.sidebarTextActive,
                backgroundColor: active ? chrome.sidebarItemActive : 'transparent',
                transition: 'background-color 120ms ease',
                '&:hover': locked ? undefined : {
                    backgroundColor: chrome.sidebarItemHover,
                },
            }}
        >
            <Icon sx={{fontSize: 16, color: chrome.sidebarIcon, flexShrink: 0}}/>
            {!sidebarCollapsed && (
                <>
                    <Typography
                        sx={{
                            flex: 1,
                            fontSize: `${t.type.small.size}px`,
                            fontWeight: 500,
                            color: chrome.sidebarTextActive,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {label}
                    </Typography>
                    {!locked && (
                        <KeyboardArrowDown sx={{fontSize: 14, color: chrome.sidebarIcon, flexShrink: 0}}/>
                    )}
                </>
            )}
        </Box>
    );

    if (sidebarCollapsed && label) {
        return (
            <Tooltip title={label} placement="right" disableInteractive>
                {row}
            </Tooltip>
        );
    }
    return row;
};

const ScopeMenu = ({anchor, open, onClose, items, selectedId, clearLabel, onSelect, onClear}) => {
    const [query, setQuery] = useState('');
    const translate = useTranslate();
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter(i => (i.name ?? '').toLowerCase().includes(q));
    }, [items, query]);

    React.useEffect(() => {
        if (!open) setQuery('');
    }, [open]);

    return (
        <Menu
            anchorEl={anchor}
            open={open}
            onClose={onClose}
            slotProps={{
                paper: {
                    sx: {
                        mt: 0.5,
                        minWidth: 240,
                        maxHeight: 380,
                        borderRadius: `${t.radius.md}px`,
                        border: `1px solid ${t.color.border.default}`,
                        boxShadow: t.shadow.dropdown,
                    },
                },
            }}
        >
            {items.length > 6 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mx: 1,
                        mt: 0.5,
                        mb: 0.5,
                        px: 1,
                        height: 32,
                        borderRadius: `${t.radius.sm}px`,
                        border: `1px solid ${t.color.border.default}`,
                    }}
                >
                    <Search sx={{fontSize: 14, color: t.color.text.muted}}/>
                    <InputBase
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={translate('Search…')}
                        sx={{
                            flex: 1,
                            fontSize: `${t.type.small.size}px`,
                        }}
                    />
                </Box>
            )}

            {selectedId != null && (
                <MenuItem
                    onClick={() => { onClear?.(); onClose(); }}
                    sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.secondary}}
                >
                    <Close sx={{fontSize: 14, mr: 1}}/>
                    {clearLabel}
                </MenuItem>
            )}

            {filtered.length === 0 && (
                <Box sx={{px: 2, py: 1.5, fontSize: `${t.type.caption.size}px`, color: t.color.text.muted}}>
                    {translate('No matches')}
                </Box>
            )}

            {filtered.map(item => {
                const isSelected = String(item.id) === String(selectedId);
                return (
                    <MenuItem
                        key={String(item.id)}
                        onClick={() => { onSelect?.(item); onClose(); }}
                        sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.primary}}
                    >
                        <Box sx={{width: 20, display: 'inline-flex', alignItems: 'center'}}>
                            {isSelected && <Check sx={{fontSize: 14, color: t.color.accent.primary}}/>}
                        </Box>
                        {item.name}
                    </MenuItem>
                );
            })}
        </Menu>
    );
};

const BrandFallback = () => {
    const {chrome, sidebarCollapsed} = useChromeMode();
    return (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            gap: 1,
            height: 52,
            px: sidebarCollapsed ? 0 : 2,
            borderBottom: `1px solid ${chrome.sidebarDivider}`,
            flexShrink: 0,
        }}
    >
        <Box
            sx={{
                width: 26,
                height: 26,
                borderRadius: `${t.radius.sm}px`,
                backgroundColor: t.color.accent.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: t.color.accent.onPrimary,
                fontWeight: 700,
                fontSize: 13,
                flexShrink: 0,
            }}
        >
            N
        </Box>
        {!sidebarCollapsed && (
            <>
                <Typography
                    sx={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: chrome.sidebarTextActive,
                        letterSpacing: '-0.01em',
                    }}
                >
                    Nofam
                </Typography>
                <Typography
                    sx={{
                        fontSize: 10,
                        fontWeight: 600,
                        px: 0.75,
                        py: 0.125,
                        borderRadius: `${t.radius.sm}px`,
                        backgroundColor: chrome.sidebarItemActive,
                        color: chrome.sidebarText,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                    }}
                >
                    Admin
                </Typography>
            </>
        )}
    </Box>
    );
};

const ScopeSwitcher = () => {
    const {permissions} = usePermissions();
    const {umbrellaId, charityId, umbrellaExternalId, charityExternalId, setUmbrella, setCharity} = useScope();
    const {chrome, sidebarCollapsed} = useChromeMode();
    const translate = useTranslate();

    const [umbrellaAnchor, setUmbrellaAnchor] = useState(null);
    const [charityAnchor, setCharityAnchor] = useState(null);

    const isAdmin = permissions === 'admin';
    const isUmbrella = permissions === 'umbrella';
    const canScope = isAdmin || isUmbrella;

    const umbrellas = useUmbrellaChoices();
    const {charities} = useUmbrellasAndCharities({umbrellaId: isAdmin ? (umbrellaId ?? 0) : 0});

    // Self-heal: localStorage scope written before slice 3 (or by a stale tab) only carries
    // numeric ids, no external ids. Once the matching choice list has loaded, backfill the
    // external id so the v2 children list gets scope narrowing again. Converges after one
    // setUmbrella/setCharity call each — the external id becomes non-null, which then blocks
    // the effect from firing again for the same selection (no loop).
    useEffect(() => {
        if (!isAdmin) return;
        if (umbrellaId == null || umbrellaExternalId != null) return;
        const matched = (umbrellas ?? []).find(u => String(u.id) === String(umbrellaId));
        if (matched?.external_id) {
            setUmbrella(umbrellaId, matched.external_id);
        }
    }, [isAdmin, umbrellaId, umbrellaExternalId, umbrellas, setUmbrella]);

    useEffect(() => {
        if (charityId == null || charityExternalId != null) return;
        const matched = (charities ?? []).find(c => String(c.id) === String(charityId));
        if (matched?.external_id) {
            setCharity(charityId, matched.external_id);
        }
    }, [charityId, charityExternalId, charities, setCharity]);

    if (!canScope) {
        return <BrandFallback/>;
    }

    const selectedUmbrella = isAdmin
        ? umbrellas.find(u => String(u.id) === String(umbrellaId))
        : null;
    const selectedCharity = (charities ?? []).find(c => String(c.id) === String(charityId));

    const umbrellaLabel = isAdmin
        ? (selectedUmbrella?.name ?? translate('All umbrellas'))
        : null;
    const charityLabel = (!isAdmin || umbrellaId != null)
        ? (selectedCharity?.charity_name ?? selectedCharity?.name ?? (isAdmin ? translate('All charities in umbrella') : translate('All my charities')))
        : null;

    const showCharityRow = !isAdmin || umbrellaId != null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.25,
                py: 1,
                borderBottom: `1px solid ${chrome.sidebarDivider}`,
                flexShrink: 0,
            }}
        >
            {!sidebarCollapsed && (
                <Typography
                    sx={{
                        px: 2,
                        mt: 0.5,
                        mb: 0.5,
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        color: chrome.sidebarHeading,
                    }}
                >
                    {translate('Scope')}
                </Typography>
            )}

            {isAdmin && (
                <ScopeRow
                    icon={CorporateFareOutlined}
                    label={umbrellaLabel}
                    active={umbrellaId != null}
                    onClick={(e) => setUmbrellaAnchor(e.currentTarget)}
                />
            )}

            {showCharityRow && (
                <ScopeRow
                    icon={ApartmentOutlined}
                    label={charityLabel}
                    active={charityId != null}
                    onClick={(e) => setCharityAnchor(e.currentTarget)}
                    indent={isAdmin}
                />
            )}

            {isAdmin && (
                <ScopeMenu
                    anchor={umbrellaAnchor}
                    open={Boolean(umbrellaAnchor)}
                    onClose={() => setUmbrellaAnchor(null)}
                    items={umbrellas ?? []}
                    selectedId={umbrellaId}
                    clearLabel={translate('All umbrellas')}
                    onSelect={(item) => setUmbrella(item.id, item.external_id ?? null)}
                    onClear={() => setUmbrella(null)}
                />
            )}

            <ScopeMenu
                anchor={charityAnchor}
                open={Boolean(charityAnchor)}
                onClose={() => setCharityAnchor(null)}
                items={(charities ?? []).map(c => ({id: c.id, name: c.charity_name ?? c.name, external_id: c.external_id}))}
                selectedId={charityId}
                clearLabel={isAdmin ? translate('All charities in umbrella') : translate('All my charities')}
                onSelect={(item) => setCharity(item.id, item.external_id ?? null)}
                onClear={() => setCharity(null)}
            />
        </Box>
    );
};

export default ScopeSwitcher;
